/**
 * SitePhaseProgress Component
 * Main progress panel showing overall site progress and phase list
 */

import React, { useState } from 'react';
import {
  SITE_STATUS,
  SITE_STATUS_LABELS,
  SITE_STATUS_COLORS,
  SITE_PHASE_STATUS,
  SITE_PHASE_STATUS_LABELS,
  SITE_PHASE_STATUS_COLORS
} from '../../utils/constants';
import InlineError from '../common/InlineError';

// Status badge component
const StatusBadge = ({ status, labels, colors, size = 'sm' }) => {
  const label = labels[status] || status;
  const color = colors[status] || 'gray';

  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  const colorClasses = {
    gray: 'bg-gray-100 text-gray-800',
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  return (
    <span className={`inline-flex font-medium rounded-full ${sizeClasses[size]} ${colorClasses[color]}`}>
      {label}
    </span>
  );
};

// Phase progress bar
const PhaseProgressBar = ({ progress, status }) => {
  const getProgressColor = () => {
    if (status === SITE_PHASE_STATUS.COMPLETED) return 'bg-green-500';
    if (status === SITE_PHASE_STATUS.IN_PROGRESS) return 'bg-blue-500';
    if (status === SITE_PHASE_STATUS.SKIPPED) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  );
};

const SitePhaseProgress = ({
  site,
  phases = [],
  onPhaseClick,
  onStartPhase,
  onCompletePhase,
  onSkipPhase,
  onUpdateProgress,
  onEditPhase,
  onSetupPhases,
  canEdit = false,
  loading = false,
  error = null,
  onRetry = null
}) => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [showAllPhases, setShowAllPhases] = useState(false);

  // Calculate overall progress
  const overallProgress = site?.overallProgress || 0;

  // Find current phase (first in_progress phase, or first pending if none in progress)
  const currentPhase = phases.find(p => p.status === SITE_PHASE_STATUS.IN_PROGRESS) ||
    phases.find(p => p.status === SITE_PHASE_STATUS.PENDING);
  const currentPhaseIndex = currentPhase ? phases.findIndex(p => p.id === currentPhase.id) : -1;

  // Stats
  const completedCount = phases.filter(p => p.status === SITE_PHASE_STATUS.COMPLETED).length;
  const skippedCount = phases.filter(p => p.status === SITE_PHASE_STATUS.SKIPPED).length;
  const inProgressCount = phases.filter(p => p.status === SITE_PHASE_STATUS.IN_PROGRESS).length;

  // Phases to display (show all or just summary)
  const displayPhases = showAllPhases ? phases : phases.slice(0, 5);

  const togglePhaseExpand = (phaseId) => {
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
    if (onPhaseClick) onPhaseClick(phaseId);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Progress</h3>
        <InlineError message={error} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Site Progress</h3>
          <StatusBadge
            status={site?.siteStatus}
            labels={SITE_STATUS_LABELS}
            colors={SITE_STATUS_COLORS}
            size="sm"
          />
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3">
          {phases.length > 0 && canEdit && (
            <button
              onClick={() => onSetupPhases && onSetupPhases()}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1.5 min-h-[44px] sm:min-h-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Reconfigure</span>
              <span className="sm:hidden">Edit</span>
            </button>
          )}
          <span className="text-3xl sm:text-2xl font-bold text-gray-900">
            {Math.round(overallProgress)}%
          </span>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>{completedCount} of {phases.length} phases completed</span>
          {skippedCount > 0 && <span>{skippedCount} skipped</span>}
        </div>
      </div>

      {/* Empty State - No Phases Configured */}
      {phases.length === 0 && (
        <div className="py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Phases Configured</h4>
          <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
            Set up phases to track progress for this site. You can choose from templates or create custom phases.
          </p>
          {canEdit && (
            <button
              onClick={() => onSetupPhases && onSetupPhases()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Setup Phases
            </button>
          )}
        </div>
      )}

      {/* Current Phase Highlight */}
      {phases.length > 0 && currentPhase && (
        <div className="mb-4 p-4 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm sm:shadow-none">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Current Phase</p>
              <p className="text-base sm:text-sm font-semibold text-blue-900 truncate">{currentPhase.name}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-2xl sm:text-lg font-bold text-blue-900">{Math.round(currentPhase.progress)}%</p>
              {currentPhase.tasks && currentPhase.tasks.length > 0 && (
                <p className="text-xs text-blue-600">
                  {currentPhase.tasks.filter(t => t.status === 'completed').length}/{currentPhase.tasks.length} tasks
                </p>
              )}
            </div>
          </div>
          {/* Current phase progress bar */}
          <div className="mt-3 sm:mt-2">
            <PhaseProgressBar progress={currentPhase.progress} status={currentPhase.status} />
          </div>
        </div>
      )}

      {/* Phase List */}
      {phases.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 mb-2">All Phases</h4>
          {displayPhases.map((phase, index) => {
          const isExpanded = expandedPhase === phase.id;
          const isCurrent = phase.id === currentPhase?.id;

          return (
            <div
              key={phase.id}
              className={`border rounded-lg transition-all duration-200 ${
                isCurrent ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Phase Row */}
              <button
                onClick={() => togglePhaseExpand(phase.id)}
                className="w-full px-3 sm:px-3 py-3 sm:py-2.5 flex items-center gap-3 text-left min-h-[56px] sm:min-h-[44px] touch-manipulation"
              >
                {/* Sequence Number */}
                <div
                  className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm sm:text-xs font-bold ${
                    phase.status === SITE_PHASE_STATUS.COMPLETED
                      ? 'bg-green-500 text-white'
                      : phase.status === SITE_PHASE_STATUS.IN_PROGRESS
                      ? 'bg-blue-500 text-white animate-pulse'
                      : phase.status === SITE_PHASE_STATUS.SKIPPED
                      ? 'bg-yellow-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {phase.status === SITE_PHASE_STATUS.COMPLETED ? (
                    <svg className="w-4 h-4 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : phase.status === SITE_PHASE_STATUS.SKIPPED ? (
                    <svg className="w-4 h-4 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  ) : (
                    phase.sequence
                  )}
                </div>

                {/* Phase Info */}
                <div className="flex-1 min-w-0">
                  {/* Mobile: Stack vertically for more space */}
                  <div className="sm:hidden">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className={`text-sm font-medium truncate ${
                        phase.status === SITE_PHASE_STATUS.COMPLETED ? 'text-green-700' :
                        phase.status === SITE_PHASE_STATUS.IN_PROGRESS ? 'text-blue-700' :
                        phase.status === SITE_PHASE_STATUS.SKIPPED ? 'text-yellow-700' :
                        'text-gray-600'
                      }`}>
                        {phase.name}
                      </span>
                      <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{Math.round(phase.progress)}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <PhaseProgressBar progress={phase.progress} status={phase.status} />
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">({phase.weight}%)</span>
                    </div>
                  </div>
                  {/* Desktop: Original horizontal layout */}
                  <div className="hidden sm:block">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${
                        phase.status === SITE_PHASE_STATUS.COMPLETED ? 'text-green-700' :
                        phase.status === SITE_PHASE_STATUS.IN_PROGRESS ? 'text-blue-700' :
                        phase.status === SITE_PHASE_STATUS.SKIPPED ? 'text-yellow-700' :
                        'text-gray-600'
                      }`}>
                        {phase.name}
                      </span>
                      <span className="text-xs text-gray-400">({phase.weight}%)</span>
                    </div>
                    {/* Compact progress */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 max-w-[120px]">
                        <PhaseProgressBar progress={phase.progress} status={phase.status} />
                      </div>
                      <span className="text-xs text-gray-500">{Math.round(phase.progress)}%</span>
                    </div>
                  </div>
                </div>

                {/* Expand Icon */}
                <svg
                  className={`w-5 h-5 sm:w-4 sm:h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-3 sm:px-3 pb-4 sm:pb-3 pt-2 sm:pt-1 border-t border-gray-100 max-h-[60vh] sm:max-h-none overflow-y-auto">
                  {/* Phase Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2 text-xs mb-3">
                    <div className="flex justify-between sm:block py-1 sm:py-0 border-b sm:border-0 border-gray-100">
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-1 font-medium">
                        {SITE_PHASE_STATUS_LABELS[phase.status]}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block py-1 sm:py-0 border-b sm:border-0 border-gray-100">
                      <span className="text-gray-500">Weight:</span>
                      <span className="ml-1 font-medium">{phase.weight}%</span>
                    </div>
                    {phase.startedAt && (
                      <div className="flex justify-between sm:block py-1 sm:py-0 border-b sm:border-0 border-gray-100">
                        <span className="text-gray-500">Started:</span>
                        <span className="ml-1 font-medium">
                          {new Date(phase.startedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {phase.completedAt && (
                      <div className="flex justify-between sm:block py-1 sm:py-0 border-b sm:border-0 border-gray-100">
                        <span className="text-gray-500">Completed:</span>
                        <span className="ml-1 font-medium">
                          {new Date(phase.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {phase.notes && (
                    <div className="text-xs text-gray-600 mb-3 p-3 sm:p-2 bg-gray-50 rounded">
                      {phase.notes}
                    </div>
                  )}

                  {/* Tasks Summary - Show 3 tasks on both mobile and desktop */}
                  {phase.tasks && phase.tasks.length > 0 && (
                    <div className="mb-4 sm:mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2 sm:mb-1">
                        Tasks ({phase.tasks.filter(t => t.status === 'completed').length}/{phase.tasks.length})
                      </p>
                      <div className="space-y-1.5 sm:space-y-1">
                        {phase.tasks.slice(0, 3).map(task => (
                          <div key={task.id} className="flex items-center gap-2 text-xs py-1 sm:py-0.5">
                            <span className={`w-4 h-4 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                              task.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                            }`} />
                            <span className={`truncate ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                              {task.title}
                            </span>
                          </div>
                        ))}
                        {phase.tasks.length > 3 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditPhase && onEditPhase(phase.id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1 touch-manipulation min-h-[32px] flex items-center"
                          >
                            View all {phase.tasks.length} tasks →
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  {canEdit && (
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                      {phase.status === SITE_PHASE_STATUS.PENDING && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onStartPhase && onStartPhase(phase.id);
                            }}
                            className="w-full sm:w-auto px-4 sm:px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-medium text-white bg-blue-600 rounded-lg sm:rounded hover:bg-blue-700 min-h-[44px] sm:min-h-[36px] touch-manipulation"
                          >
                            Start Phase
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSkipPhase && onSkipPhase(phase.id);
                            }}
                            className="w-full sm:w-auto px-4 sm:px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-medium text-yellow-700 bg-yellow-100 rounded-lg sm:rounded hover:bg-yellow-200 min-h-[44px] sm:min-h-[36px] touch-manipulation"
                          >
                            Skip Phase
                          </button>
                        </>
                      )}
                      {phase.status === SITE_PHASE_STATUS.IN_PROGRESS && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onCompletePhase && onCompletePhase(phase.id);
                          }}
                          className="w-full sm:w-auto px-4 sm:px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-medium text-white bg-green-600 rounded-lg sm:rounded hover:bg-green-700 min-h-[44px] sm:min-h-[36px] touch-manipulation"
                        >
                          Complete Phase
                        </button>
                      )}
                      {(phase.status === SITE_PHASE_STATUS.IN_PROGRESS ||
                        phase.status === SITE_PHASE_STATUS.COMPLETED) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateProgress && onUpdateProgress(phase.id);
                          }}
                          className="w-full sm:w-auto px-4 sm:px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-medium text-blue-700 bg-blue-100 rounded-lg sm:rounded hover:bg-blue-200 min-h-[44px] sm:min-h-[36px] touch-manipulation"
                        >
                          Update Progress
                        </button>
                      )}
                      {/* Edit button - available for all phases */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPhase && onEditPhase(phase.id);
                        }}
                        className="w-full sm:w-auto px-4 sm:px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-medium text-gray-700 bg-gray-100 rounded-lg sm:rounded hover:bg-gray-200 min-h-[44px] sm:min-h-[36px] touch-manipulation"
                      >
                        Edit Phase
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Show More/Less */}
        {phases.length > 5 && (
          <button
            onClick={() => setShowAllPhases(!showAllPhases)}
            className="w-full mt-2 py-3 sm:py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1.5 min-h-[48px] sm:min-h-[44px] touch-manipulation bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none"
          >
            {showAllPhases ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Show Less
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show All {phases.length} Phases
              </>
            )}
          </button>
        )}
        </div>
      )}

      {/* Summary Stats */}
      {phases.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-2 text-center">
            <div className="p-2 sm:p-0 rounded-lg sm:rounded-none bg-green-50 sm:bg-transparent">
              <p className="text-xl sm:text-lg font-bold text-green-600">{completedCount}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
            <div className="p-2 sm:p-0 rounded-lg sm:rounded-none bg-blue-50 sm:bg-transparent">
              <p className="text-xl sm:text-lg font-bold text-blue-600">{inProgressCount}</p>
              <p className="text-xs text-gray-500">In Progress</p>
            </div>
            <div className="p-2 sm:p-0 rounded-lg sm:rounded-none bg-yellow-50 sm:bg-transparent">
              <p className="text-xl sm:text-lg font-bold text-yellow-600">{skippedCount}</p>
              <p className="text-xs text-gray-500">Skipped</p>
            </div>
            <div className="p-2 sm:p-0 rounded-lg sm:rounded-none bg-gray-50 sm:bg-transparent">
              <p className="text-xl sm:text-lg font-bold text-gray-600">
                {phases.length - completedCount - inProgressCount - skippedCount}
              </p>
              <p className="text-xs text-gray-500">Pending</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitePhaseProgress;
